import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Inbox, Tag, FolderOpen, RefreshCw, Star, Archive, Trash2, Circle, Send, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { GmailLabel } from '../../types/api';
import { SkeletonLoader } from '../SkeletonLoader';
import { Tooltip } from '@mui/material'; // Assuming MUI or similar is available, or we can use a simple title attribute for now

interface LabelListProps {
  labels: GmailLabel[];
  selectedLabel: string | null;
  onSelectLabel: (labelId: string | null) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  activeCount?: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Container = styled.div<{ $collapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${props => props.theme.colors.backgroundSecondary};
  border-right: 1px solid ${props => props.theme.colors.border};
  transition: width 0.3s ease;
  width: ${props => props.$collapsed ? '70px' : '100%'};
`;

const Header = styled.div<{ $collapsed?: boolean }>`
  padding: ${props => props.$collapsed
    ? `${props.theme.spacing[3]} ${props.theme.spacing[2]}`
    : `${props.theme.spacing[3]} ${props.theme.spacing[3]} ${props.theme.spacing[3]} ${props.theme.spacing[2]}`};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: ${props => props.$collapsed ? 'center' : 'space-between'};
  gap: ${props => props.theme.spacing[2]};
  min-height: 57px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconButton = styled.button`
  border: 1px solid ${props => props.theme.colors.border};
  background: transparent;
  border-radius: ${props => props.theme.borders.radius.md};
  padding: ${props => props.theme.spacing[2]};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.backgroundTertiary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => `${props.theme.spacing[2]} ${props.theme.spacing[2]} ${props.theme.spacing[4]} ${props.theme.spacing[1]}`};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[4]};

  /* Hide scrollbar for cleaner look */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[1]};
`;

const SectionTitle = styled.span<{ $collapsed?: boolean }>`
  font-size: 12px;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0 ${props => props.theme.spacing[2]} 0 ${props => props.theme.spacing[1]};
  
  ${props => props.$collapsed && css`
    display: none;
  `}
`;

const LabelButton = styled.button<{ $active: boolean; $highlight?: boolean; $collapsed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.$collapsed ? 'center' : 'space-between'};
  gap: ${props => props.theme.spacing[2]};
  width: 100%;
  padding: ${props => props.$collapsed
    ? props.theme.spacing[2]
    : `${props.theme.spacing[2]} ${props.theme.spacing[2]} ${props.theme.spacing[2]} ${props.theme.spacing[1]}`};
  background: ${props => props.$active ? props.theme.colors.primary + '20' : 'transparent'};
  border: 1px solid ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  border-radius: ${props => props.theme.borders.radius.md};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary + '25' : props.theme.colors.backgroundTertiary};
  }

  .label-info {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing[2]};
    flex: 1;
    min-width: 0;
    justify-content: ${props => props.$collapsed ? 'center' : 'flex-start'};
  }

  .label-name {
    font-size: 13px;
    font-weight: ${props => props.$active ? props.theme.typography.fontWeight.semibold : props.theme.typography.fontWeight.medium};
    color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: ${props => props.$collapsed ? 'none' : 'block'};
  }

  .label-counts {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing[1]};
    font-size: 11px;
    color: ${props => props.$highlight ? props.theme.colors.error : props.theme.colors.textSecondary};
    font-weight: ${props => props.$highlight ? props.theme.typography.fontWeight.semibold : props.theme.typography.fontWeight.medium};
    display: ${props => props.$collapsed ? 'none' : 'flex'};
  }

  /* Dot indicator for unread messages in collapsed mode */
  ${props => props.$collapsed && props.$highlight && css`
    &::after {
      content: '';
      position: absolute;
      top: 6px;
      right: 6px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${props.theme.colors.error};
    }
  `}
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing[4]};
  font-size: 13px;
  color: ${props => props.theme.colors.textSecondary};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px dashed ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  text-align: center;
`;

const CollapseButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: -12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s;
  box-shadow: ${props => props.theme.shadows.sm};

  &:hover {
    color: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: scale(1.1);
  }
`;

const Footer = styled.div<{ $collapsed?: boolean }>`
  padding: ${props => props.theme.spacing[3]};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: ${props => props.$collapsed ? 'center' : 'flex-end'};
`;

const getDefaultIcon = (labelId: string) => {
  switch (labelId) {
    case 'INBOX':
      return <Inbox size={18} />;
    case 'STARRED':
      return <Star size={18} />;
    case 'SENT':
      return <Send size={18} />;
    case 'DRAFT':
      return <Archive size={18} />;
    case 'IMPORTANT':
      return <Star size={18} />;
    case 'TRASH':
      return <Trash2 size={18} />;
    case 'SPAM':
      return <AlertTriangle size={18} />;
    case 'UNREAD':
      return <Circle size={14} fill="#ef4444" color="#ef4444" />;
    case 'IMPORTANT_UNREAD':
      return <Circle size={14} fill="#facc15" color="#facc15" />;
    default:
      return <Tag size={18} />;
  }
};

const formatCount = (value?: number) => {
  if (typeof value !== 'number') return '0';
  return new Intl.NumberFormat('pt-BR').format(value);
};

const getFriendlyName = (label: GmailLabel) => {
  if (!label.name) return label.id;

  const map: Record<string, string> = {
    'CATEGORY_PROMOTIONS': 'Promoções',
    'CATEGORY_SOCIAL': 'Social',
    'CATEGORY_UPDATES': 'Atualizações',
    'CATEGORY_FORUMS': 'Fóruns',
    'CATEGORY_PERSONAL': 'Pessoal',
    'CATEGORY_RESERVATIONS': 'Reservas',
    'CATEGORY_TRAVEL': 'Viagens',
    'CATEGORY_FINANCE': 'Finanças',
    'CATEGORY_PURCHASES': 'Compras',
  };

  return map[label.id] || label.name;
};

export const LabelList: React.FC<LabelListProps> = ({
  labels,
  selectedLabel,
  onSelectLabel,
  onRefresh,
  isLoading = false,
  activeCount,
  collapsed = false,
  onToggleCollapse,
}) => {
  const systemLabels = useMemo(
    () => labels.filter(label => label.type === 'system'),
    [labels]
  );

  const userLabels = useMemo(
    () => labels.filter(label => label.type !== 'system'),
    [labels]
  );

  const resolveCount = (label: GmailLabel) => {
    // Se a label está selecionada e temos activeCount, use-o
    if (selectedLabel === label.id && typeof activeCount === 'number') {
      return activeCount;
    }

    // Para INBOX, sempre mostre não lidas se disponível
    if (label.id === 'INBOX' && label.messagesUnread != null) {
      return label.messagesUnread;
    }

    // Para outras labels, priorize total, depois não lidas
    if (label.messagesTotal != null && label.messagesTotal > 0) {
      return label.messagesTotal;
    }

    if (label.messagesUnread != null && label.messagesUnread > 0) {
      return label.messagesUnread;
    }

    return 0;
  };

  const allMessagesCount = (() => {
    if (selectedLabel === null && typeof activeCount === 'number') {
      return activeCount;
    }
    return labels.reduce((acc, label) => acc + (label.messagesTotal ?? 0), 0);
  })();

  const renderLabelButton = (
    id: string | null,
    name: string,
    icon: React.ReactNode,
    count?: number | string,
    highlight?: boolean
  ) => (
    <LabelButton
      key={id || 'all'}
      type="button"
      onClick={() => onSelectLabel(id)}
      $active={selectedLabel === id}
      $highlight={highlight}
      $collapsed={collapsed}
      title={collapsed ? name : undefined}
    >
      <div className="label-info">
        {icon}
        <span className="label-name">{name}</span>
      </div>
      <div className="label-counts">{count}</div>
    </LabelButton>
  );

  return (
    <Container $collapsed={collapsed}>
      <Header $collapsed={collapsed}>
        {!collapsed && <Title>Caixas</Title>}
        <div style={{ display: 'flex', gap: 8 }}>
          {!collapsed && (
            <IconButton
              onClick={() => onRefresh?.()}
              disabled={isLoading || !onRefresh}
              aria-label="Atualizar labels"
              title="Atualizar"
            >
              <RefreshCw />
            </IconButton>
          )}
          {onToggleCollapse && (
            <IconButton onClick={onToggleCollapse} title={collapsed ? "Expandir" : "Recolher"}>
              {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </IconButton>
          )}
        </div>
      </Header>

      <List>
        <Section>
          <SectionTitle $collapsed={collapsed}>Principais</SectionTitle>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <SkeletonLoader key={index} height={40} borderRadius="12px" />
            ))
          ) : (
            <>
              {renderLabelButton(
                'INBOX',
                'Caixa de entrada',
                <Inbox size={18} />,
                formatCount(systemLabels.find(label => label.id === 'INBOX')?.messagesUnread),
                true
              )}

              {systemLabels
                .filter(label => label.id !== 'INBOX')
                .map(label =>
                  renderLabelButton(
                    label.id,
                    getFriendlyName(label),
                    getDefaultIcon(label.id),
                    formatCount(resolveCount(label)),
                    label.id === 'UNREAD'
                  )
                )}
            </>
          )}
        </Section>

        <Section>
          <SectionTitle $collapsed={collapsed}>Labels</SectionTitle>

          {isLoading && userLabels.length === 0 ? (
            Array.from({ length: 3 }).map((_, index) => (
              <SkeletonLoader key={index} height={36} borderRadius="10px" />
            ))
          ) : userLabels.length === 0 ? (
            !collapsed && <EmptyState>Nenhuma label criada.</EmptyState>
          ) : (
            userLabels.map(label =>
              renderLabelButton(
                label.id,
                label.name,
                <Tag size={18} />,
                formatCount(resolveCount(label))
              )
            )
          )}
        </Section>

        <Section>
          <SectionTitle $collapsed={collapsed}>Outros</SectionTitle>
          {renderLabelButton(
            null,
            'Todas as mensagens',
            <FolderOpen size={18} />,
            formatCount(allMessagesCount)
          )}
        </Section>
      </List>

      {/* Optional footer if needed later */}
    </Container>
  );
};
